/**
 * MonthlyReportView - Relatório mensal de atividades da propriedade
 */

import React, { useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MonthlyReportView = ({ property, month }) => {
  const { t } = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const monthNames = [
    t('rptMonthJan'), t('rptMonthFeb'), t('rptMonthMar'), t('rptMonthApr'),
    t('rptMonthMay'), t('rptMonthJun'), t('rptMonthJul'), t('rptMonthAug'),
    t('rptMonthSep'), t('rptMonthOct'), t('rptMonthNov'), t('rptMonthDec')
  ];

  const formatMonth = (monthStr) => {
    try {
      if (!monthStr || typeof monthStr !== 'string') return t('rptMonthNotInformed');
      const [year, monthNum] = monthStr.split('-');
      if (!year || !monthNum) return monthStr;
      const monthIndex = parseInt(monthNum) - 1;
      if (monthIndex < 0 || monthIndex >= 12) return monthStr;
      return `${monthNames[monthIndex]} ${year}`;
    } catch {
      return monthStr || t('rptErrorDisplay');
    }
  };

  // Nº real de dias do mês selecionado (evita fixar 30)
  const diasNoMes = (() => {
    try {
      const [y, m] = (month || '').split('-').map(Number);
      if (!y || !m) return 30;
      return new Date(y, m, 0).getDate();
    } catch {
      return 30;
    }
  })();

  // Calcular estatísticas por trabalhador a partir dos registros + eventos do calendário
  const computeWorkerStats = (records, events) => {
    const statsMap = {};
    const safeRecords = Array.isArray(records) ? records : [];

    // Inicializar/agregar a partir dos registros de sangria
    safeRecords.forEach(r => {
      const name = r.trabalhador_responsavel || '—';
      if (!statsMap[name]) {
        statsMap[name] = { name, bleedings: 0, lost: 0, production: 0, absences: 0, compensated: 0 };
      }
      if (r.sangria) statsMap[name].bleedings += 1;
      if (r.sangria_perdida && r.sangria_perdida > 0) statsMap[name].lost += (r.sangria_perdida || 0);
      statsMap[name].production += r.producao_kg || 0;
    });

    // Agregar faltas
    (events || []).filter(e => e.event_type === 'falta').forEach(f => {
      const name = f.trabalhador_responsavel || '—';
      if (!statsMap[name]) {
        statsMap[name] = { name, bleedings: 0, lost: 0, production: 0, absences: 0, compensated: 0 };
      }
      statsMap[name].absences += 1;
      if (f.compensada) statsMap[name].compensated += 1;
    });

    // Retornar só trabalhadores com nome (excluir "—")
    return Object.values(statsMap)
      .filter(s => s.name && s.name !== '—')
      .sort((a, b) => b.production - a.production);
  };

  const loadMonthlyReport = React.useCallback(async () => {
    if (!property || !month) return;
    setLoading(true);
    
    try {
      const [reportRes, summaryRes, eventsRes, recordsRes, teamV2Res] = await Promise.all([
        axios.get(`${API}/bleeding-records/${property.id}/monthly-report?month=${month}`),
        axios.get(`${API}/bleeding-records/${property.id}/summary?month=${month}`),
        axios.get(`${API}/calendar-events`, { params: { property_id: property.id, month } }),
        axios.get(`${API}/bleeding-records/${property.id}?month=${month}`),
        axios.get(`${API}/bleeding-records/${property.id}/team-report-v2?month=${month}`).catch(() => ({ data: null }))
      ]);
      
      const enhancedReportData = {
        ...reportRes.data,
        summary_metrics: summaryRes.data?.summary || {},
        all_records: recordsRes.data || [],
        team_v2: teamV2Res.data || null  // dados do novo endpoint (modo equipe/individual/flexível)
      };
      
      setReportData(enhancedReportData);
      setCalendarEvents(eventsRes.data || []);
      setShowReport(true);
    } catch (error) {
      console.error("Error loading monthly report:", error);
      alert(t('errorLoading') + ": " + (error.response?.data?.detail || error.message));
      setShowReport(false);
    }
    
    setLoading(false);
    setTimeout(() => setLoading(false), 100);
  }, [property, month, t]);

  if (!property) return null;

  return (
    <Card className="mb-6 border-green-200 bg-green-50" data-testid="monthly-report">
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span className="flex items-center text-green-800">
            {t('monthlyReport')} - {property.name}
          </span>
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              loadMonthlyReport();
            }} 
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-primary-foreground shadow h-9 px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
            data-testid="generate-report-btn"
          >
            {loading ? t('generating') : t('generateReport')}
          </button>
        </CardTitle>
      </CardHeader>
      
      {showReport && reportData && (
        <CardContent>
          <div className="space-y-6">
            {/* Header Information */}
            <div className="grid md:grid-cols-2 gap-4 bg-white p-4 rounded-lg border">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">{t('rptPropertyInfo')}</h3>
                <p><strong>{t('rptName')}:</strong> {property.name}</p>
                <p><strong>{t('rptLocation')}:</strong> {property.location}</p>
                <p><strong>{t('rptCultureAge')}:</strong> {property.culture_age_years} {t('rptYears')}</p>
                {property.tempo_exploracao_anos && (
                  <p><strong>{t('rptExplorationTime')}:</strong> {property.tempo_exploracao_anos} {t('rptYears')}</p>
                )}
                {property.total_area_hectares && (
                  <p><strong>{t('rptTotalArea')}:</strong> {property.total_area_hectares} {t('rptHectares')}</p>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">{t('reportPeriod')}</h3>
                <p><strong>{t('rptMonthYear')}:</strong> {formatMonth(month)}</p>
                <p><strong>{t('rptTotalRecords')}:</strong> {reportData.total_records || 0}</p>
                <p><strong>{t('rptGeneratedAt')}:</strong> {new Date().toLocaleDateString()}</p>
              </div>
            </div>

            {/* Summary Metrics */}
            <div className="bg-white p-4 rounded-lg border">
              <h3 className="font-semibold text-gray-800 mb-4">{t('rptGeneralSummary')}</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{reportData.total_records || 0}</div>
                  <div className="text-sm text-gray-600">{t('rptTotalRecordsLabel')}</div>
                  <div className="text-xs text-blue-500 mt-1">
                    {((reportData.total_records || 0) / diasNoMes * 100).toFixed(0)}% {t('rptOfMonth')}
                  </div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {reportData.total_production_kg || reportData.summary_metrics?.total_producao_kg || 0}kg
                  </div>
                  <div className="text-sm text-gray-600">{t('rptTotalProduction')}</div>
                  <div className="text-xs text-green-500 mt-1">
                    {reportData.total_records > 0 ? 
                      Math.round((reportData.total_production_kg || reportData.summary_metrics?.total_producao_kg || 0) / (reportData.total_records || 1)) + t('rptPerRecord')
                      : t('rptNoData')
                    }
                  </div>
                </div>
                <div className={`text-center p-3 rounded-lg border ${
                  (reportData.worker_evaluations?.length || 0) > 0 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className={`text-2xl font-bold ${
                    (reportData.worker_evaluations?.length || 0) > 0 
                      ? 'text-green-600' 
                      : 'text-gray-500'
                  }`}>
                    {reportData.worker_evaluations?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">{t('rptEvaluatedWorkers')}</div>
                  <div className={`text-xs mt-1 ${
                    (reportData.worker_evaluations?.length || 0) > 0 
                      ? 'text-green-500' 
                      : 'text-gray-400'
                  }`}>
                    {(reportData.worker_evaluations?.length || 0) > 0 ? t('rptWithEval') : t('rptWithoutEval')}
                  </div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{reportData.month || 'N/A'}</div>
                  <div className="text-sm text-gray-600">{t('reportMonth')}</div>
                </div>
              </div>

              {/* Bleeding Activities */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <img src="/icons/faca_sangria.png" alt="" className="w-5 h-5" /> {t('rptBleedingActivities')}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xl font-bold text-green-600">{reportData.summary_metrics?.total_sangria_dias || 0}</div>
                    <div className="text-xs text-gray-600">{t('rptDaysPerformed')}</div>
                    <div className="text-xs text-green-500 mt-1">
                      {((reportData.summary_metrics?.total_sangria_dias || 0) / diasNoMes * 100).toFixed(0)}% {t('rptOfMonth')}
                    </div>
                  </div>
                  <div className={`text-center p-3 rounded-lg border ${
                    (reportData.summary_metrics?.total_sangria_perdida || 0) === 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className={`text-xl font-bold ${
                      (reportData.summary_metrics?.total_sangria_perdida || 0) === 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {reportData.summary_metrics?.total_sangria_perdida || 0}
                    </div>
                    <div className="text-xs text-gray-600">{t('rptLostBleedings')}</div>
                    <div className={`text-xs mt-1 ${
                      (reportData.summary_metrics?.total_sangria_perdida || 0) === 0
                        ? 'text-green-500'
                        : 'text-red-500'
                    }`}>
                      {(reportData.summary_metrics?.total_sangria_perdida || 0) === 0 ? t('rptExcellent') : t('rptAttention')}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="text-xl font-bold text-emerald-600">
                      {((reportData.summary_metrics?.total_sangria_dias || 0) / 
                        Math.max(((reportData.summary_metrics?.total_sangria_dias || 0) + (reportData.summary_metrics?.total_sangria_perdida || 0)), 1) * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-600">{t('rptApprovalRate')}</div>
                    <div className="text-xs text-emerald-500 mt-1">
                      {((reportData.summary_metrics?.total_sangria_dias || 0) / 
                        Math.max(((reportData.summary_metrics?.total_sangria_dias || 0) + (reportData.summary_metrics?.total_sangria_perdida || 0)), 1) * 100) >= 90 
                        ? t('rptGreat') : t('rptCanImprove')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Weather */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">{t('rptWeatherConditions')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-sky-50 rounded-lg border border-sky-200">
                    <div className="text-xl font-bold text-sky-600">{reportData.summary_metrics?.total_chuva_mm || 0}mm</div>
                    <div className="text-xs text-gray-600">{t('rptTotalRain')}</div>
                    <div className="text-xs text-sky-500 mt-1">
                      {((reportData.summary_metrics?.total_chuva_mm || 0) / (reportData.summary_metrics?.total_sangria_dias || 1)).toFixed(1)}{t('rptPerActiveDay')}
                    </div>
                  </div>
                  <div className={`text-center p-3 rounded-lg border ${
                    (reportData.summary_metrics?.total_reposicao || 0) === 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className={`text-xl font-bold ${
                      (reportData.summary_metrics?.total_reposicao || 0) === 0
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>
                      {reportData.summary_metrics?.total_reposicao || 0}
                    </div>
                    <div className="text-xs text-gray-600">{t('rptTotalReplacements')}</div>
                    <div className={`text-xs mt-1 ${
                      (reportData.summary_metrics?.total_reposicao || 0) === 0
                        ? 'text-green-500'
                        : 'text-yellow-500'
                    }`}>
                      {(reportData.summary_metrics?.total_reposicao || 0) === 0 ? t('rptNoReplacements') : t('rptReplacementsDone')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Complementary Activities */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3">{t('rptComplementaryAct')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <div className={`text-center p-3 rounded-lg border ${
                    (reportData.summary_metrics?.total_fungicida_aplicacoes || 0) > 0
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`text-xl font-bold ${
                      (reportData.summary_metrics?.total_fungicida_aplicacoes || 0) > 0
                        ? 'text-green-600'
                        : 'text-gray-500'
                    }`}>
                      {reportData.summary_metrics?.total_fungicida_aplicacoes || 0}
                    </div>
                    <div className="text-xs text-gray-600">{t('rptFungicideApp')}</div>
                    <div className={`text-xs mt-1 ${
                      (reportData.summary_metrics?.total_fungicida_aplicacoes || 0) > 0
                        ? 'text-green-500'
                        : 'text-gray-400'
                    }`}>
                      {(reportData.summary_metrics?.total_fungicida_aplicacoes || 0) > 0 ? t('rptApplied') : t('rptNotApplied')}
                    </div>
                  </div>
                  <div className={`text-center p-3 rounded-lg border ${
                    (reportData.summary_metrics?.total_ethrel_aplicacoes || 0) > 0
                      ? 'bg-teal-50 border-teal-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`text-xl font-bold ${
                      (reportData.summary_metrics?.total_ethrel_aplicacoes || 0) > 0
                        ? 'text-teal-600'
                        : 'text-gray-500'
                    }`}>
                      {reportData.summary_metrics?.total_ethrel_aplicacoes || 0}
                    </div>
                    <div className="text-xs text-gray-600">{t('rptEthrelApp')}</div>
                    <div className={`text-xs mt-1 ${
                      (reportData.summary_metrics?.total_ethrel_aplicacoes || 0) > 0
                        ? 'text-teal-500'
                        : 'text-gray-400'
                    }`}>
                      {(reportData.summary_metrics?.total_ethrel_aplicacoes || 0) > 0 ? t('rptApplied') : t('rptNotApplied')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* NOVA SEÇÃO: Por Trabalhador (Cortes + Sangrias + Faltas) — V2 com modo_sangria */}
            {(() => {
              const teamV2 = reportData.team_v2;
              const usaV2 = teamV2 && teamV2.sangradores && teamV2.sangradores.length > 0;

              // Map de faltas (do calendar_events) por trabalhador
              const faltasPorTrab = {};
              (calendarEvents || []).filter(e => e.event_type === 'falta').forEach(f => {
                const n = f.trabalhador_responsavel || '—';
                if (!faltasPorTrab[n]) faltasPorTrab[n] = { absences: 0, compensated: 0 };
                faltasPorTrab[n].absences += 1;
                if (f.compensada) faltasPorTrab[n].compensated += 1;
              });

              // Modo V2: usa o backend, enriquece com faltas
              if (usaV2) {
                const rowsRaw = teamV2.sangradores.map(s => ({
                  name: s.nome,
                  cortes: s.cortes,
                  sangrias_dias: s.sangrias_dias,
                  lotes: s.lotes || [],
                  reposicoes: s.reposicoes || 0,
                  lost: s.perdidas || 0,
                  production: s.producao_kg || 0,
                  g_por_corte: s.g_por_corte,
                  absences: (faltasPorTrab[s.nome]?.absences) || 0,
                  compensated: (faltasPorTrab[s.nome]?.compensated) || 0,
                  is_team_row: !!s.is_team_row,
                }));

                // Separa linha de "Equipe (sem responsável)" das linhas dos sangradores
                const teamRow = rowsRaw.find(r => r.is_team_row);
                const namedRows = rowsRaw.filter(r => !r.is_team_row);

                // Decisão por modo:
                // - equipe: esconde a linha team (redundante) → mostra nota discreta
                // - individual: mantém em VERMELHO (é anomalia — registro sem responsável)
                // - flexivel/outros: mantém em amarelo (aceitável)
                const modo = teamV2.modo_sangria;
                let rows = namedRows;
                let teamRowStyle = 'bg-amber-50 italic';
                let teamAlert = null;

                if (teamRow) {
                  if (modo === 'equipe') {
                    // Não mostra a linha; adiciona nota discreta abaixo do banner
                    teamAlert = (
                      <p className="text-xs text-gray-500 mb-3 italic" data-testid="team-anon-note">
                        Registros anônimos do mês ({teamRow.sangrias_dias} sangria(s) + {teamRow.reposicoes} reposição(ões){teamRow.lost > 0 ? ` + ${teamRow.lost} perdida(s)` : ''}) foram distribuídos entre os {namedRows.length} sangrador(es) nomeado(s) da equipe.
                      </p>
                    );
                  } else if (modo === 'individual') {
                    // Modo individual: linha anônima é BUG de cadastro → destaque vermelho
                    teamRowStyle = 'bg-red-50 italic';
                    teamAlert = (
                      <div className="text-xs text-red-700 mb-3 p-2 bg-red-50 border border-red-200 rounded" data-testid="team-anon-alert">
                        ⚠️ <strong>{teamRow.sangrias_dias} registro(s)</strong> sem responsável identificado. Em modo individual, cada dia deveria ter um sangrador atribuído. Considere atribuir esses registros aos sangradores corretos.
                      </div>
                    );
                    rows = [...namedRows, teamRow];
                  } else {
                    // Modo flexivel/outros: mantém no amarelo, sem alarme
                    rows = [...namedRows, teamRow];
                  }
                }

                return (
                  <div className="bg-white p-4 rounded-lg border" data-testid="worker-stats-section">
                    <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                      👥 {t('rptWorkerSectionTitle') || 'Por trabalhador'}
                      <span className="text-xs font-normal text-gray-500 ml-2">
                        Modo: <strong>{teamV2.modo_sangria === 'equipe' ? '🤝 Equipe' : teamV2.modo_sangria === 'individual' ? '👤 Individual' : '🔄 Flexível'}</strong>
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 mb-4">
                      💡 <strong>Cortes</strong> = dias de sangria + reposições (parciais contam, ex: 0,5+0,5=1). Dia com 2 lotes sangrados conta 1 corte — o lote extra vira crédito de recuperação no Aproveitamento. {teamV2.modo_sangria === 'equipe' ? 'Todos os sangradores nomeados recebem o total da equipe (todos trabalham juntos).' : 'Cada trabalhador conta apenas seus próprios cortes.'}
                    </p>
                    {/* Banner de Plantas Ativas / Paralisadas — só aparece com sangradores_alocacao configurado */}
                    {teamV2.plantas_ativas_no_mes != null && (
                      <div
                        className={`mb-4 p-3 rounded-lg border ${
                          (teamV2.plantas_paralisadas || 0) > 0
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-emerald-50 border-emerald-200'
                        }`}
                        data-testid="paralisacao-banner"
                      >
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className={`font-semibold ${(teamV2.plantas_paralisadas || 0) > 0 ? 'text-amber-800' : 'text-emerald-800'}`}>
                            🌳 Plantas ativas no mês: <strong>{(teamV2.plantas_ativas_no_mes || 0).toLocaleString('pt-BR')}</strong>
                          </span>
                          {(teamV2.plantas_paralisadas || 0) > 0 && (
                            <span className="text-amber-700">
                              • <strong>{teamV2.plantas_paralisadas.toLocaleString('pt-BR')}</strong> paralisadas
                            </span>
                          )}
                          {teamV2.total_plantas_propriedade && (
                            <span className="text-gray-600 text-xs">
                              (total da safra: {teamV2.total_plantas_propriedade.toLocaleString('pt-BR')})
                            </span>
                          )}
                        </div>
                        {(teamV2.sangradores_inativos_no_mes || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-amber-200">
                            <p className="text-xs text-amber-700 mb-1">
                              <strong>Sangradores sem registros neste mês:</strong>
                            </p>
                            <ul className="text-xs text-amber-700 space-y-0.5" data-testid="sangradores-inativos-list">
                              {teamV2.sangradores_inativos_no_mes.map((s, i) => (
                                <li key={i}>
                                  • <strong>{s.nome}</strong> ({s.plantas?.toLocaleString('pt-BR')} plantas) — {s.motivo}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {teamAlert}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-2">Trabalhador</th>
                            <th className="text-center p-2" title="Soma de sangrias[].valor + reposicoes[].valor">Cortes ⓘ</th>
                            <th className="text-center p-2" title="Nº de dias com sangria registrada para ele">Sangrias</th>
                            <th className="text-center p-2">Lotes</th>
                            <th className="text-center p-2">Reposições</th>
                            <th className="text-center p-2">Perdidas</th>
                            <th className="text-center p-2">Faltas</th>
                            <th className="text-center p-2">Compens.</th>
                            <th className="text-center p-2">g/árvore/corte</th>
                            <th className="text-center p-2">Produção</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((w, idx) => (
                            <tr key={idx} className={`border-b hover:bg-gray-50 ${w.is_team_row ? teamRowStyle : ''}`} data-testid={`worker-row-${idx}`}>
                              <td className="p-2 font-medium">{w.is_team_row ? '👥 ' + w.name : w.name}</td>
                              <td className="text-center p-2 text-green-700 font-semibold">{w.cortes}</td>
                              <td className="text-center p-2 text-gray-700">{w.sangrias_dias}</td>
                              <td className="text-center p-2 text-xs">{w.lotes.length > 0 ? w.lotes.join(', ') : '-'}</td>
                              <td className="text-center p-2">
                                <span className={w.reposicoes > 0 ? 'text-blue-600 font-semibold' : 'text-gray-400'}>{w.reposicoes}</span>
                              </td>
                              <td className="text-center p-2">
                                <span className={w.lost > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{w.lost}</span>
                              </td>
                              <td className="text-center p-2">
                                <span className={w.absences > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{w.absences}</span>
                              </td>
                              <td className="text-center p-2">
                                <span className={w.compensated > 0 ? 'text-blue-600 font-semibold' : 'text-gray-400'}>{w.compensated}</span>
                              </td>
                              <td className="text-center p-2 text-xs">{w.g_por_corte != null ? `${w.g_por_corte} g` : '-'}</td>
                              <td className="text-center p-2 font-medium">{(w.production || 0).toFixed(1)} kg</td>
                            </tr>
                          ))}
                          {/* Linha de total */}
                          <tr className="border-t-2 bg-gray-100 font-semibold">
                            <td className="p-2">TOTAL</td>
                            <td className="text-center p-2 text-green-700">{teamV2.cortes_equipe_total}</td>
                            <td className="text-center p-2">{teamV2.dias_sangria_propriedade}</td>
                            <td className="text-center p-2 text-xs">—</td>
                            <td className="text-center p-2">—</td>
                            <td className="text-center p-2">—</td>
                            <td className="text-center p-2">—</td>
                            <td className="text-center p-2">—</td>
                            <td className="text-center p-2">—</td>
                            <td className="text-center p-2">{teamV2.total_producao_kg} kg</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }

              // Fallback: lógica legacy (caso o endpoint v2 não responda)
              const workerStats = computeWorkerStats(reportData.all_records, calendarEvents);
              if (workerStats.length === 0) return null;
              return (
                <div className="bg-white p-4 rounded-lg border" data-testid="worker-stats-section">
                  <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                    👥 {t('rptWorkerSectionTitle') || 'Por trabalhador'}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    💡 Sangrias factuais + ausências registradas. As faltas mostram impacto direto na produção individual.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-2">{t('rptWorkerName') || 'Trabalhador'}</th>
                          <th className="text-center p-2">{t('rptWorkerBleedings') || 'Sangrias'}</th>
                          <th className="text-center p-2">{t('rptWorkerLost') || 'Perdidas'}</th>
                          <th className="text-center p-2">{t('rptWorkerAbsences') || 'Faltas'}</th>
                          <th className="text-center p-2">{t('rptWorkerCompensated') || 'Compensadas'}</th>
                          <th className="text-center p-2">{t('rptWorkerProduction') || 'Produção'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workerStats.map((w, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50" data-testid={`worker-row-${idx}`}>
                            <td className="p-2 font-medium">{w.name}</td>
                            <td className="text-center p-2 text-green-700 font-semibold">{w.bleedings}</td>
                            <td className="text-center p-2">
                              <span className={w.lost > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                                {w.lost}
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span className={w.absences > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>
                                {w.absences}
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span className={w.compensated > 0 ? 'text-blue-600 font-semibold' : 'text-gray-400'}>
                                {w.compensated}
                                {w.absences > 0 && (
                                  <span className="text-[10px] text-gray-500 ml-1">
                                    ({Math.round((w.compensated / w.absences) * 100)}%)
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="text-center p-2 font-medium">{w.production.toFixed(1)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* NOVA SEÇÃO: Ausências e Feriados do período */}
            {(() => {
              const faltas = calendarEvents.filter(e => e.event_type === 'falta');
              const feriados = calendarEvents.filter(e => e.event_type === 'feriado');
              if (faltas.length === 0 && feriados.length === 0) return null;
              return (
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Faltas */}
                  <div className="bg-white p-4 rounded-lg border" data-testid="absences-section">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      🟡 {t('rptAbsencesTitle') || 'Ausências do período'}
                      <span className="text-xs text-gray-500 font-normal">({faltas.length})</span>
                    </h3>
                    {faltas.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('rptNoAbsences') || 'Nenhuma falta registrada no período.'}</p>
                    ) : (
                      <ul className="space-y-1.5 text-sm max-h-60 overflow-y-auto">
                        {faltas.map((f) => (
                          <li key={f.id} className="flex items-start gap-2 py-1 border-b border-gray-100 last:border-0">
                            <span className="text-xs text-gray-500 font-mono min-w-[70px]">
                              {new Date(f.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </span>
                            <div className="flex-1">
                              <span className="font-medium text-gray-800">{f.trabalhador_responsavel}</span>
                              {f.motivo && <span className="text-gray-500 text-xs ml-1">— {f.motivo}</span>}
                              {f.compensada && (
                                <span className="ml-2 text-[10px] text-green-700 font-semibold">✓ compensada</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Feriados */}
                  <div className="bg-white p-4 rounded-lg border" data-testid="holidays-section">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      ⬜ {t('rptHolidaysTitle') || 'Feriados'}
                      <span className="text-xs text-gray-500 font-normal">({feriados.length})</span>
                    </h3>
                    {feriados.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('rptNoHolidays') || 'Nenhum feriado registrado no período.'}</p>
                    ) : (
                      <ul className="space-y-1.5 text-sm max-h-60 overflow-y-auto">
                        {feriados.map((f) => (
                          <li key={f.id} className="flex items-start gap-2 py-1 border-b border-gray-100 last:border-0">
                            <span className="text-xs text-gray-500 font-mono min-w-[70px]">
                              {new Date(f.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </span>
                            <span className="flex-1 text-gray-800">
                              {f.description || (t('celHoliday') || 'Feriado')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Worker Evaluations */}
            <div className="bg-white p-4 rounded-lg border">
              <h3 className="font-semibold text-gray-800 mb-4">{t('rptWorkerEvals')}</h3>
              {reportData.worker_evaluations && reportData.worker_evaluations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">{t('rptWorker')}</th>
                        <th className="text-center p-2">{t('rptAverage')}</th>
                        <th className="text-center p-2">{t('rptDays')}</th>
                        <th className="text-center p-2">{t('rptStatus')}</th>
                        <th className="text-left p-2">{t('rptRecommendation')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.worker_evaluations.map((worker, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{worker.name}</td>
                          <td className="text-center p-2">
                            <span className={`px-2 py-1 rounded text-white ${
                              worker.average_score >= 8 ? 'bg-green-600' :
                              worker.average_score >= 7 ? 'bg-blue-600' :
                              worker.average_score >= 6 ? 'bg-yellow-600' : 'bg-red-600'
                            }`}>
                              {worker.average_score}/10
                            </span>
                          </td>
                          <td className="text-center p-2">{worker.days_worked}</td>
                          <td className="text-center p-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              worker.status === 'EXCELENTE' ? 'bg-green-100 text-green-800' :
                              worker.status === 'BOM' ? 'bg-blue-100 text-blue-800' :
                              worker.status === 'REGULAR' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {worker.status}
                            </span>
                          </td>
                          <td className="p-2 text-xs">{worker.recommendation}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('rptNoWorkerEval')}</p>
                  <p className="text-xs mt-2">{t('rptActivateInternal')}</p>
                </div>
              )}
            </div>

            {/* Recommendations */}
            <div className="bg-white p-4 rounded-lg border">
              <h3 className="font-semibold text-gray-800 mb-3">{t('rptRecommendations')}</h3>
              {reportData.recommendations && reportData.recommendations.length > 0 ? (
                <div className="space-y-2">
                  {reportData.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start space-x-2 p-2 bg-blue-50 rounded">
                      <span className="text-blue-600 font-medium">•</span>
                      <span className="text-sm text-blue-800">{recommendation}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">{t('rptNoRecommendations')}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowReport(false)} data-testid="close-report-btn">
                {t('closeReport')}
              </Button>
              <Button 
                onClick={() => window.print()} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="print-report-btn"
              >
                {t('savePdfPrint')}
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default MonthlyReportView;
